import { useEffect, useState } from "react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import type { AdToken } from "./adTokens";

/**
 * The ad token vocabulary, fetched once for the whole page and shared by every field.
 *
 * The registry is global reference data curated by hand in Django admin, so there is nothing
 * to refetch against: one request on first use, an in-memory list from then on, and a page
 * refresh to pick up a registry change. Deliberately not react-query -- one list that never
 * invalidates does not need a cache library.
 *
 * The promise is the cache, not its result, so four fields mounting in the same tick share
 * one request instead of racing four.
 */
let vocabulary: Promise<AdToken[]> | null = null;

/**
 * Fail soft, but do not remember the failure. A list that never arrived is not a list: the
 * editor checks every bracket against it (MPN-1930), so caching `[]` for the page's lifetime
 * would turn one blip into an editor that cannot check anything until a reload. A failed
 * read is forgotten and the next caller to mount asks again. That is once per mount, never
 * per keystroke -- one call covers all four fields.
 */
export function loadAdTokens(): Promise<AdToken[]> {
    if (!vocabulary) {
        const attempt: Promise<AdToken[]> = Promise.resolve()
            .then(() => new ContentCatalogService().listAdTokens())
            .catch((error: unknown) => {
                if (vocabulary === attempt) vocabulary = null;
                throw error;
            });
        vocabulary = attempt;
    }
    return vocabulary;
}

/** Drops the cached vocabulary. For tests -- nothing in the app refetches. */
export function resetAdTokens(): void {
    vocabulary = null;
}

/**
 * Whether there is a vocabulary to check the copy against.
 *
 *   loading     -- not here yet. Say nothing: a note that flashes on every open is noise.
 *   ready       -- here, with at least one token in it.
 *   unavailable -- the read failed, or the registry is empty. Empty is real data, but
 *                  checking against it would call every bracket in the copy unknown.
 */
export type AdTokenStatus = "loading" | "ready" | "unavailable";

export type AdTokenVocabulary = {
    /** Empty until it arrives, and empty if it never does. The picker never opens on empty. */
    tokens: AdToken[];
    status: AdTokenStatus;
};

/** `enabled` false reads nothing and stays loading -- for a field whose ad tokens are flagged off (MPN-2014). */
export function useAdTokens(enabled = true): AdTokenVocabulary {
    const [state, setState] = useState<AdTokenVocabulary>({ tokens: [], status: "loading" });

    useEffect(() => {
        if (!enabled) return;
        let live = true;
        loadAdTokens().then(
            (tokens) => {
                if (live) setState({ tokens, status: tokens.length ? "ready" : "unavailable" });
            },
            () => {
                if (live) setState({ tokens: [], status: "unavailable" });
            },
        );
        return () => {
            live = false;
        };
    }, [enabled]);

    return state;
}
